// Extraído de `aplicarPatchReagendado` (FlujoCajaV2/index.js) — nace en Fase 1c (Commit 4b) como
// función pura que recibía `(resultado, estadoActual)` y devolvía el bundle de estado YA MERGEADO.
// Fase 1d (auditoría independiente, fisura de severidad ALTA — 2026-09-24) retira `estadoActual` de
// la firma: `estadoActual` era el estado vigente en el momento en que se armó la LLAMADA (capturado en
// un closure de React del render del click) — si entre el click y que la escritura en DB resolvía
// pasaba OTRO reagendado (de otra fila del mismo tipo, o de la misma fila dos veces) o un refetch de
// `cargarDatosFuturos` completaba en el medio, el segundo patch se calculaba sobre una COPIA VIEJA y
// el `setX(valorYaCalculado)` (no funcional) pisaba cualquier cambio llegado después de capturar ese
// closure — "última escritura gana sobre una copia vieja", el bug clásico de no usar el patrón
// funcional de `setState`.
//
// Firma nueva: `patchReagendado(resultado)` — YA NO recibe estado. Devuelve, por cada `_patchType`,
// un `updaters`: un mapa `{ nombreSlice: (prev) => nuevoValor }` donde CADA función parte del `prev`
// que React le pasa EN EL MOMENTO REAL en que corre el `setState` (nunca de una copia capturada
// antes) — el patrón funcional estándar de React. El caller (`aplicarPatchReagendado`,
// FlujoCajaV2/index.js) pasa cada updater DIRECTO a su `setX` (`setX(updaters.slice)`), nunca un valor
// ya calculado — así dos reagendados concurrentes (o un reagendado + un refetch de
// `cargarDatosFuturos` en el medio) se aplican en el orden en que React los procesa, cada uno sobre el
// estado más reciente, sin pisarse. Misma lógica de matching/cálculo que antes (mismo criterio por
// ID/clave compuesta, 10.7; mismos campos que cambian) — la única diferencia es `(prev) =>
// prev.map(...)` en vez de `estadoActual.slice.map(...)`.
//
// Tres `_patchType` no son puramente síncronos/computables (side-effects reales, no solo cálculo):
//   - "sueldo": no hay id escalar simple para un patch por-fila (la fila de sueldo depende de un
//     índice derivado, `fechasPagoPorEmpMes`) — el refresh real es un refetch DIRIGIDO async
//     (`getFechasPagoPais`). Forzar esto a ser síncrono/puro significaría mover el refetch acá, lo
//     que acopla un util de cálculo a I/O de red — peor que la alternativa.
//   - "cuota_activo" (Fase 3b, fisura B1, 2026-09-25): reagendar UNA cuota de `plan_pagos_activos`
//     puede reordenar el `_posicion` ("Cuota N/M") de TODAS las cuotas hermanas del plan — un `.map`
//     puntual sobre la cuota movida no recalcula eso, así que este case también pasa a refetch
//     DIRIGIDO por `activo_id` (`getCuotasActivosFuturas`), mismo motivo que "sueldo".
//   - `_patchType` no reconocido (bug: `reagendarEntryFuturo` devolvió algo que este handler no sabe
//     aplicar): requiere `console.error` + `toast.error`, dos side-effects que no pertenecen a una
//     función de cálculo puro.
// Para esos tres, `patchReagendado` devuelve un EFECTO declarativo — `{ efecto: "refetch_sueldo" }` /
// `{ efecto: "refetch_cuotas_activo", activo_id, fecha_planificada }` / `{ efecto: "no_reconocido",
// resultado }` — que el caller interpreta y ejecuta. Sigue siendo una función 100% PURA: sin
// `setState`, sin `toast`, sin `async` — los updaters son funciones que SE VAN A pasar a `setState`,
// pero `patchReagendado` misma nunca llama a ningún setter ni dispara I/O.
//
// Caso "pipeline" con `moneda_dolar`: necesita el país operativo para convertir a CLP
// (`getDolarValue(fecha, pais)`). Como `patchReagendado` ya no recibe el bundle de estado (que antes
// traía `estadoActual.pais`), el país viaja DENTRO del propio `resultado` — `reagendarEntryFuturo` ya
// lo conoce (`ctx.pais`, el mismo país con el que se validó y escribió el reagendado) y lo agrega al
// patch que devuelve para `_patchType: "pipeline"`. Es el país VIGENTE en el momento del click, tan
// "capturado" como el resto de los datos del patch (id, nueva fecha) — no reintroduce la carrera que
// motivó este cambio: el bug era pisar ARRAYS completos con una copia vieja del ARRAY, no usar el país
// con el que se armó ESTE patch puntual (el país no es un slice concurrente que otro reagendado pueda
// pisar en el medio).
import { getDolarValue } from "./exchangeRateService";

/**
 * @param {Object} resultado - lo que devuelve `reagendarEntryFuturo` (trae `_patchType`).
 * @returns {Object} `{ updaters: { nombreSlice: (prev) => nuevoValor, ... } }` (cero o más slices,
 *   cada updater se pasa DIRECTO a su `setX`) o `{ efecto: "refetch_sueldo" }` / `{ efecto:
 *   "no_reconocido", resultado }` para los dos casos declarativos de arriba.
 */
/**
 * Devuelve los nombres de slice de `updaters` que NO tienen un setter correspondiente en
 * `nombresSetters` (Fase 2, Commit 0 — cierra una deuda de la Fase 1d: antes `aplicarPatchReagendado`
 * ignoraba en silencio cualquier slice sin setter). Un slice sin setter significa que
 * `patchReagendado` y `aplicarPatchReagendado` (FlujoCajaV2/index.js) se desincronizaron — alguien
 * agregó un `_patchType` nuevo en un lado (con un nombre de slice nuevo en su `updaters`) sin
 * actualizar el mapa `setters` del otro lado. Eso NUNCA debe pasar desapercibido (10.177): el caller
 * usa esto para rechazar EXPLÍCITO en vez de aplicar solo una parte del patch sin avisar.
 * @param {Object} updaters - `{ nombreSlice: (prev) => nuevo }`, lo que devuelve `patch.updaters`.
 * @param {string[]} nombresSetters - las claves del mapa `setters` del caller.
 * @returns {string[]} nombres de slice sin setter (vacío si todo matchea).
 */
export const slicesSinSetter = (updaters, nombresSetters) => {
  const disponibles = new Set(nombresSetters || []);
  return Object.keys(updaters || {}).filter((nombreSlice) => !disponibles.has(nombreSlice));
};

/**
 * Evalúa un `matchEsperado` (`{ slice, cuota_id }`, ver `patch.matchEsperado` en los casos
 * "cuota_cobro"/"cuota_pago" de arriba) contra el valor REAL del slice — Fase 2b, Commit 2. Función
 * pura, mismo estilo declarativo que `slicesSinSetter`: el caller (`aplicarPatchReagendado`,
 * FlujoCajaV2/index.js) la corre ANTES de aplicar ningún `setX`, con el valor actual del slice que
 * `matchEsperado.slice` nombra (`cuotasCobrosPendientes`/`cuotasPagosPendientes`), y rechaza explícito
 * si devuelve `false` — nunca aplica un patch cuyo `cuota_id` no está en el slice (10.177: la
 * escritura en DB ya ocurrió, pero la UI no reflejaría nada y nadie se enteraría).
 * @param {{slice:string, cuota_id:number}|undefined} matchEsperado - `patch.matchEsperado`.
 * @param {Array} valorSliceReal - el valor ACTUAL del slice que `matchEsperado.slice` nombra.
 * @returns {boolean} `true` si no hay `matchEsperado` que evaluar, o si el `cuota_id` SÍ está en el
 *   slice; `false` si `matchEsperado` está presente y el `cuota_id` NO aparece.
 */
export const matchEsperadoCumplido = (matchEsperado, valorSliceReal) => {
  if (!matchEsperado) return true;
  return (valorSliceReal || []).some((c) => c.id === matchEsperado.cuota_id);
};

// Commit 4a (auditoría 3b/3c/4, 2026-09-25) — fallback compartido de "cuota_cobro"/"cuota_pago":
// preferir el dato fresco de `resultado.cuota` (la fila que devolvió la RPC), y si viene ausente,
// conservar el valor previo de la fila local (`c`) — nunca un `null` a ciegas (10.177).
const fechaOriginalConservada = (resultado, c) =>
  resultado.cuota?.fecha_vencimiento_original ?? c.fecha_vencimiento_original;

export const patchReagendado = (resultado) => {
  switch (resultado?._patchType) {
    case "facturado":
      // Espejo de PlanCuotasModal onSuccess (accion "fecha", tipo "cobro").
      return {
        updaters: {
          pendientesCobro: (prev) =>
            prev.map((p) =>
              p.id === resultado.id ? { ...p, plazocobro: resultado.plazocobro, _plazoOverride: true } : p
            ),
        },
      };

    case "proveedor_sii":
      // Espejo de PlanCuotasModal onSuccess (accion "fecha", SII recibida) + el segundo setState de
      // ese mismo onSuccess (Fase 1c, Commit 2c): pasar a pago único borra el plan de cuotas en
      // backend → sacar cualquier cuota residual de esta factura del estado local (10.7).
      return {
        updaters: {
          proveedoresPendientes: (prev) =>
            prev.map((p) => (p.id === resultado.id ? { ...p, plazopago: resultado.plazopago } : p)),
          cuotasPagosPendientes: (prev) => prev.filter((c) => c.factura_sii_recibida_id !== resultado.id),
        },
      };

    case "proveedor_externa":
      // Espejo de PlanCuotasModal onSuccess (accion "fecha", externa) + Commit 2c, por `factura_externa_id`.
      return {
        updaters: {
          facturasExternasPendientes: (prev) =>
            prev.map((p) => (p.id === resultado.id ? { ...p, plazopago: resultado.plazopago } : p)),
          cuotasPagosPendientes: (prev) => prev.filter((c) => c.factura_externa_id !== resultado.id),
        },
      };

    case "proveedor_boleta":
      // Fase 4 de "Futuros: reagendar" (2026-09-25) — espejo EXACTO de "proveedor_sii"/"proveedor_externa"
      // de arriba, mismo patrón (10.1): match por ID real (10.7) — `resultado.boleta_id` es el id
      // numérico de `boletas_honorarios`, nunca el `id` string sintético de la entry. También limpia
      // cualquier cuota residual de `cuotasPagosPendientes` por `factura_boleta_id` — defensa en
      // profundidad por si el estado local tuviera cuotas stale de un plan que `guardarFechaFactura`
      // ya borró en DB (mismo motivo que las otras dos ramas; en el caso normal, una boleta que llega
      // acá nunca tuvo cuotas, así que este filter es un no-op).
      return {
        updaters: {
          boletasHonorariosPendientes: (prev) =>
            prev.map((b) => (b.id === resultado.boleta_id ? { ...b, plazopago: resultado.plazopago } : b)),
          cuotasPagosPendientes: (prev) => prev.filter((c) => c.factura_boleta_id !== resultado.boleta_id),
        },
      };

    case "capex_activo":
      // Espejo de ModificarFechaProyeccionCapexModal onSaved.
      return {
        updaters: {
          capexEntries: (prev) =>
            prev.map((a) =>
              a.id === resultado.activo_id ? { ...a, fecha_planificada: resultado.fecha_planificada } : a
            ),
        },
      };

    case "capex_lote_componente":
      // Espejo de ModificarFechaLoteCapexModal onSaved (rama costo_id).
      return {
        updaters: {
          componentesLotes: (prev) =>
            prev.map((c) => (c.id === resultado.costo_id ? { ...c, fecha_pago: resultado.fecha_pago } : c)),
        },
      };

    case "capex_lote_fob":
      // Espejo de ModificarFechaLoteCapexModal onSaved (rama FOB).
      return {
        updaters: {
          capexLotes: (prev) =>
            prev.map((l) =>
              l.id === resultado.lote_id ? { ...l, fecha_planificada: resultado.fecha_planificada } : l
            ),
        },
      };

    case "costo_proyecto":
      // Fase 3 de "Costos de proyectos de Cartelería" (2026-10-03) — SOLO la rama sin plan (ver
      // reagendarEntryFuturo.js): patch directo por id (10.7/10.234), sin trigger de "original" que
      // conservar (un costo sin plan nunca trackea override, igual que "capex_activo" sin plan).
      return {
        updaters: {
          costosProyectoFuturos: (prev) =>
            (prev || []).map((c) =>
              c.id === resultado.costo_proyecto_id ? { ...c, fecha_prevista: resultado.fecha_prevista } : c
            ),
        },
      };

    case "edificio":
      // Espejo de PlanPagoEdificioModal onSuccess (op "editar").
      return {
        updaters: {
          planPagosEdificios: (prev) => prev.map((p) => (p.id === resultado.planId ? { ...p, ...resultado.row } : p)),
        },
      };

    case "compromiso":
      // Espejo de ModificarFechaProyeccionCompromisoModal onSaved — SOLO fecha (el monto no se toca
      // desde este flujo, a diferencia del modal viejo que también edita monto).
      return {
        updaters: {
          compromisosEntries: (prev) =>
            prev.map((c) =>
              c.id === resultado.compromiso_id ? { ...c, fecha_estimada: resultado.fecha_estimada } : c
            ),
        },
      };

    case "recurrente": {
      // Espejo de EditarFechaRecurrenteModal onSaved (match por clave compuesta, no por id escalar — 10.7).
      const disc = resultado.costo_asociado_id ?? "main";
      return {
        updaters: {
          proveedoresRecurrentes: (prev) =>
            prev.map((r) => {
              const rDisc = r.costo_asociado_id ?? "main";
              if (
                r.proveedor_id !== resultado.proveedor_id ||
                r.periodo_año !== resultado.periodo_año ||
                r.periodo_mes !== resultado.periodo_mes ||
                rDisc !== disc
              ) {
                return r;
              }
              return { ...r, fecha_display: resultado.nuevaFecha, tiene_override: resultado.tieneOverride };
            }),
        },
      };
    }

    case "cuota_cobro":
      // Fase 2 de "Futuros: reagendar" (Commit 3): reagenda UNA cuota de plan_cobros_facturas por
      // `cuota_id` — match exclusivo por id (10.7), nunca por posición ni por factura_sii_emitida_id,
      // así que las cuotas hermanas del mismo plan quedan EXACTAMENTE intactas (misma referencia).
      //
      // `matchEsperado` (Fase 2b, Commit 2 — cierra una deuda de la Fase 2 original): la escritura en
      // DB ya se confirmó por `cuota_id` (guardarFechaCuotaFactura releyó y actualizó esa fila real) —
      // pero el slice local `cuotasCobrosPendientes` puede NO tener esa cuota (loader distinto,
      // filtro de país/corte, o un refetch que la sacó en el medio). Si el `.map()` no encuentra
      // ningún match, la UI se queda visualmente igual aunque el UPDATE en DB funcionó — un éxito
      // silencioso (10.177). `matchEsperado` es un descriptor PURO (mismo espíritu que
      // `slicesSinSetter` — un chequeo declarativo, no un side-effect) que el caller
      // (`aplicarPatchReagendado`, FlujoCajaV2/index.js) evalúa contra el valor REAL del slice ANTES
      // de aplicar nada, y rechaza explícito si no hay match — reusa el MISMO estilo de
      // "precheck declarativo + reject antes de aplicar" que ya usa `slicesSinSetter`, no un mecanismo
      // paralelo.
      //
      // Fase 3c (2026-09-25): además de `fecha_vencimiento`, copia `fecha_vencimiento_original` (la
      // fila completa que devolvió la RPC, propagada por `reagendarEntryFuturo.js` — el trigger de la
      // migración 20260925b ya la capturó DENTRO del mismo UPDATE) — así el mapper (`esFechaModificada`,
      // fechasFuturosPanel.js) recalcula `tiene_override` con el dato REAL en el próximo render, sin
      // esperar un refetch: la pill pasa a azul (o queda sin color, si la cuota volvió a su fecha
      // original) al instante.
      //
      // Auditoría 3b/3c/4, Commit 4a (2026-09-25): `resultado.cuota?.fecha_vencimiento_original ??
      // c.fecha_vencimiento_original` — NUNCA `?? null`. `resultado.cuota` es la fila cruda que
      // devolvió la RPC (puede venir ausente si el writer no la trajo); si falta, se conserva el valor
      // que la cuota YA tenía en el estado local (`c`, la fila previa a este patch) en vez de pisarlo
      // con un `null` firme — un dato ausente nunca borra un original real ya visible (10.177).
      return {
        updaters: {
          cuotasCobrosPendientes: (prev) =>
            prev.map((c) =>
              c.id === resultado.cuota_id
                ? {
                    ...c,
                    fecha_vencimiento: resultado.nuevaFecha,
                    fecha_vencimiento_original: fechaOriginalConservada(resultado, c),
                  }
                : c
            ),
        },
        matchEsperado: { slice: "cuotasCobrosPendientes", cuota_id: resultado.cuota_id },
      };

    case "cuota_pago":
      // Espejo de "cuota_cobro" — plan_pagos_facturas cubre las tres variantes del lado pago (SII
      // recibida/externa/boleta), todas viven en el mismo slice `cuotasPagosPendientes`.
      return {
        updaters: {
          cuotasPagosPendientes: (prev) =>
            prev.map((c) =>
              c.id === resultado.cuota_id
                ? {
                    ...c,
                    fecha_vencimiento: resultado.nuevaFecha,
                    fecha_vencimiento_original: fechaOriginalConservada(resultado, c),
                  }
                : c
            ),
        },
        matchEsperado: { slice: "cuotasPagosPendientes", cuota_id: resultado.cuota_id },
      };

    case "cuota_activo":
      // Fase 3 de "Futuros: reagendar" (Commit 2, 2026-09-25), REDISEÑADO en Fase 3b (auditoría
      // independiente, fisura B1, 2026-09-25): antes esto era un `updaters` que solo pisaba
      // `fecha_vencimiento` de LA cuota movida por id (10.7) — pero la columna "Cuota N/M" (`cuota_actual`/
      // `total_cuotas`) se deriva de `_posicion`/`total_cuotas`, calculados por `getCuotasActivosFuturas`
      // (`posicionesCuotas`) a partir del ORDEN de `fecha_vencimiento` de TODAS las cuotas del plan — un
      // `updaters.map` que solo toca la fecha de una cuota deja el `_posicion` de ESA cuota (y de sus
      // hermanas, si el reagendado las reordenó) desactualizado hasta el próximo refetch completo.
      // Efecto declarativo (mismo estilo que "sueldo": no hay id escalar simple para "recalcular el
      // ordinal de todo el plan" sin refetch, así que este case también deja de ser puro cálculo) — el
      // caller (`aplicarPatchReagendado`, FlujoCajaV2/index.js) hace el refetch DIRIGIDO por `activo_id`
      // (`getCuotasActivosFuturas(pais, {activoId, saldosCapex})`, 10.234, mismo patrón que el `onSaved`
      // de `PlanPagosActivoModal`, 10.259) — trae TODAS las cuotas del activo con su `_posicion`
      // recién calculada, y reemplaza el subconjunto de `cuotasActivosPendientes` de ese `activo_id`
      // completo (nunca una sola cuota suelta). `activo_id` viaja acá para que el caller sepa qué
      // subconjunto refetchear; `fecha_planificada` viaja para el segundo eje (`capexEntries`, la línea
      // proyectada del activo, mapCapex — puede venir `null` en el caso límite defensivo de un plan
      // vaciado, el caller solo aplica ese updater si no es `null`, mismo criterio que antes).
      return {
        efecto: "refetch_cuotas_activo",
        activo_id: resultado.activo_id,
        fecha_planificada: resultado.fecha_planificada,
      };

    case "sueldo":
      // No hay id escalar simple para un patch por-fila (la fila de sueldo depende de
      // `fechasPagoPorEmpMes`, un índice derivado) — el caller hace el refetch dirigido real.
      return { efecto: "refetch_sueldo" };

    case "pipeline":
      // Espejo de EditarFechaCobroModal onSaved. `resultado.pais` (agregado por `reagendarEntryFuturo`,
      // ver cabecera del archivo) reemplaza el `estadoActual.pais` que este case usaba antes.
      return {
        updaters: {
          pipeline: (prev) =>
            prev.map((p) => {
              if (p.id !== resultado.id) return p;
              const next = {
                ...p,
                fecha_estimada: resultado.fecha_estimada,
                fecha_override_actual: resultado.fecha_override_actual,
                tiene_override: resultado.tiene_override,
              };
              if (next.moneda_dolar) {
                next.importeCLP = Math.round(
                  Number(next.importe || 0) * (getDolarValue(next.fecha_estimada, resultado.pais) || 1)
                );
              }
              return next;
            }),
        },
      };

    default:
      // 10.177: un _patchType desconocido es un bug (reagendarEntryFuturo devolvió algo que este
      // handler no sabe aplicar) — nunca se traga en silencio, aunque la escritura en DB ya haya
      // ocurrido con éxito (el toast de éxito ya se mostró en CeldaFechaReagendable).
      return { efecto: "no_reconocido", resultado };
  }
};
